export type RasterInvoiceKind = 'jpeg' | 'png' | 'webp' | 'tiff';

export type InvoicePageFileKind = 'pdf' | RasterInvoiceKind;

const JPEG_MIMES = new Set(['image/jpeg', 'image/jpg']);
const PNG_MIMES = new Set(['image/png']);
const WEBP_MIMES = new Set(['image/webp']);
const TIFF_MIMES = new Set(['image/tiff', 'image/tif']);

export function fileKindFromFile(file: File): InvoicePageFileKind | null {
    const t = (file.type || '').toLowerCase().trim();
    if (t === 'application/pdf') return 'pdf';
    if (JPEG_MIMES.has(t)) return 'jpeg';
    if (PNG_MIMES.has(t)) return 'png';
    if (WEBP_MIMES.has(t)) return 'webp';
    if (TIFF_MIMES.has(t)) return 'tiff';
    const name = file.name.toLowerCase();
    if (name.endsWith('.pdf')) return 'pdf';
    if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'jpeg';
    if (name.endsWith('.png')) return 'png';
    if (name.endsWith('.webp')) return 'webp';
    if (name.endsWith('.tif') || name.endsWith('.tiff')) return 'tiff';
    return null;
}

function isJpegCompatible(kind: InvoicePageFileKind): boolean {
    return kind === 'jpeg' || kind === 'tiff';
}

/** Returns an error message if files are not all the same kind, or null if valid. */
export function validateHomogeneousPageFiles(files: File[], existingKind?: InvoicePageFileKind | null): string | null {
    if (files.length === 0) return null;
    const kinds = files.map(fileKindFromFile);
    if (kinds.some((k) => k == null)) {
        return 'One or more files are not a supported type (PDF, JPEG, PNG, WebP, or TIF).';
    }
    const first = existingKind ?? kinds[0]!;
    if (first === 'pdf') {
        if (files.length !== 1 || kinds[0] !== 'pdf') {
            return 'PDF invoices must be replaced with a single PDF file.';
        }
        return null;
    }
    if (kinds.some((k) => k === 'pdf')) {
        return 'Do not mix PDFs with image files.';
    }
    const mismatch = kinds.some((k) => {
        if (k == null) return true;
        if (k === first) return false;
        return !(isJpegCompatible(first) && isJpegCompatible(k));
    });
    if (mismatch) {
        return 'All images must use the same format (JPEG/TIF together, PNG with PNG, WebP with WebP).';
    }
    return null;
}
